import { isAdminRole, type Permission, type SessionUser } from '@hm/contracts'
import type { MessageKey } from '~/i18n'

/**
 * Who is offered a control, in one place.
 *
 * Two different questions live here, and mixing them up is the bug this module
 * exists to prevent. `can` asks what an account is *allowed* to do; `isBuyer` and
 * `isSeller` ask which side of the desk it is *on*.
 *
 * The desk holds every permission in the matrix, so a permission test hands it
 * every counterparty control too — a basket it has no business to hold, a reorder
 * the server answers with "customers only". Both need a counterparty identity
 * (`buyerRefOf` in @hm/core), which nobody on the platform side has. Whose account
 * it is, is the actual question.
 */

export const can = (permission: Permission) => (user: SessionUser) =>
  user.permissions.includes(permission)

export const isBuyer = (user: SessionUser) => user.orgType === 'buyer'
export const isSeller = (user: SessionUser) => user.orgType === 'seller'
export const isDesk = (user: SessionUser) => isAdminRole(user.role)

/**
 * What the Advanced switch actually turns on, named per role.
 *
 * The names are written out rather than derived from the screens, deliberately:
 * this is read by somebody deciding whether to flip the switch, so it has to say
 * "bulk publish" rather than "an AdvancedPanel on the pricing route". Adding an
 * advanced section elsewhere means adding a line here.
 *
 * It lives beside the access rules rather than on the settings screen because the
 * walkthrough reads it too — it skips its Advanced step when the list would be
 * empty — and two copies of the list is how one of them goes stale.
 */
export const ADVANCED_FEATURES: { label: MessageKey; show: (user: SessionUser) => boolean }[] = [
  { label: 'settings.advanced.price_books', show: can('price_book:manage') },
  { label: 'settings.advanced.reports', show: can('report:read') },
  { label: 'settings.advanced.export', show: can('data:export') },
  { label: 'settings.advanced.bulk_publish', show: can('admin_listing:manage') },
  { label: 'settings.advanced.ledger', show: can('ledger:read') },
  // A shop's own price list. The desk holds `listing:manage_own` as well, but the
  // import writes rows keyed to the caller's own org, so the screen behind this is
  // a shop's alone — offering it to the desk promises a refusal.
  { label: 'settings.advanced.price_import', show: isSeller },
]

export const advancedFeaturesFor = (user: SessionUser) =>
  ADVANCED_FEATURES.filter((feature) => feature.show(user))

export const hasAdvanced = (user: SessionUser) => advancedFeaturesFor(user).length > 0
